/**
 * BIDashboardModal
 * Owner-only business intelligence dashboard
 * Revenue, performance, driver scorecards, billing metrics
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import {
  getKPIs, getRevenueByCustomer, getRevenueByDriver,
  getDailyTrend, getOnTimePerformance, getBillingMetrics, getDriverScorecard,
} from '../services/biService';

function BIDashboardModal({ onClose }) {
  const { isOwner } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [revenueByCustomer, setRevenueByCustomer] = useState([]);
  const [revenueByDriver, setRevenueByDriver] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [onTime, setOnTime] = useState(null);
  const [billing, setBilling] = useState(null);
  const [scorecard, setScorecard] = useState([]);

  useEffect(() => { loadAll(); }, [days]);

  const loadAll = async () => {
    setLoading(true);
    const [k, rc, rd, dt, ot, bl, sc] = await Promise.all([
      getKPIs(days), getRevenueByCustomer(days), getRevenueByDriver(days),
      getDailyTrend(days), getOnTimePerformance(days), getBillingMetrics(days), getDriverScorecard(days),
    ]);
    setKpis(k); setRevenueByCustomer(rc); setRevenueByDriver(rd);
    setDailyTrend(dt); setOnTime(ot); setBilling(bl); setScorecard(sc);
    setLoading(false);
  };

  if (!isOwner) {
    return (
      <div style={modalStyle} onClick={onClose}>
        <div style={contentStyle} onClick={e => e.stopPropagation()}>
          <p style={{ color: '#f44336', textAlign: 'center', padding: '2rem' }}>Owner access required</p>
        </div>
      </div>
    );
  }

  const fmt = (n) => typeof n === 'number' ? n.toLocaleString('en-CA', { minimumFractionDigits: 0 }) : n;
  const fmtMoney = (n) => '$' + (typeof n === 'number' ? n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');

  // Simple bar renderer (pure CSS, no charting lib needed)
  const Bar = ({ value, max, color = '#4caf50', label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
      <div style={{ width: '120px', fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ flex: 1, backgroundColor: 'var(--surface-2)', borderRadius: '4px', height: '20px', overflow: 'hidden' }}>
        <div style={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, height: '100%', backgroundColor: color, borderRadius: '4px', minWidth: value > 0 ? '2px' : '0', transition: 'width 0.3s' }} />
      </div>
      <div style={{ width: '80px', fontSize: '0.8rem', color: 'var(--text-primary)', textAlign: 'right' }}>{typeof value === 'number' && value > 100 ? fmtMoney(value) : fmt(value)}</div>
    </div>
  );

  const KPICard = ({ label, value, sub, color = 'var(--accent-primary)' }) => (
    <div style={{ backgroundColor: 'var(--surface-2)', borderRadius: '8px', padding: '1rem', borderLeft: `4px solid ${color}`, minWidth: '140px' }}>
      <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>{sub}</div>}
    </div>
  );

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={{ ...contentStyle, maxWidth: '1100px' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ margin: 0, color: 'var(--accent-primary)' }}>📊 Business Intelligence</h2>
            <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Revenue, performance, and operational insights
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <select value={days} onChange={e => setDays(Number(e.target.value))} style={selectStyle}>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
            <button onClick={onClose} style={closeBtn}>×</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem', borderBottom: '2px solid var(--surface-2)', paddingBottom: '0.5rem', flexWrap: 'wrap' }}>
          {[
            { id: 'overview', label: '📈 Overview' },
            { id: 'revenue', label: '💰 Revenue' },
            { id: 'drivers', label: '🚛 Drivers' },
            { id: 'billing', label: '🧾 Billing' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === tab.id ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none', borderRadius: '4px', cursor: 'pointer',
              fontWeight: activeTab === tab.id ? 'bold' : 'normal', fontSize: '0.85rem',
            }}>
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--accent-primary)' }}>Loading analytics...</div>
        ) : (
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>

            {/* ══════ OVERVIEW TAB ══════ */}
            {activeTab === 'overview' && kpis && (
              <div>
                {/* KPI Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  <KPICard label="Total Loads" value={fmt(kpis.totalLoads)} sub={`${kpis.avgLoadsPerDay}/day avg`} />
                  <KPICard label="Completed" value={fmt(kpis.completedLoads)} sub={`${kpis.completionRate}% rate`} color="#4caf50" />
                  <KPICard label="Revenue" value={fmtMoney(kpis.totalRevenue)} sub={`${fmtMoney(kpis.avgRevenuePerLoad)}/load`} color="#2196f3" />
                  <KPICard label="Billed" value={fmtMoney(kpis.billedRevenue)} color="#9c27b0" />
                  <KPICard label="Unbilled" value={fmtMoney(kpis.unbilledRevenue)} sub="Ready to invoice" color={kpis.unbilledRevenue > 0 ? '#ff9800' : '#4caf50'} />
                  <KPICard label="Active Drivers" value={kpis.activeDrivers} color="#00bcd4" />
                  {onTime && <KPICard label="On-Time %" value={`${onTime.onTimeRate}%`} sub={`${onTime.onTime}/${onTime.total} deliveries`} color={parseFloat(onTime.onTimeRate) >= 90 ? '#4caf50' : '#ff9800'} />}
                  <KPICard label="Active Now" value={kpis.activeLoads} sub="In progress" color="#ff9800" />
                </div>

                {/* Daily Trend */}
                {dailyTrend.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Daily Load Volume</h3>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '120px', padding: '0 0.5rem' }}>
                      {dailyTrend.map((d, i) => {
                        const maxLoads = Math.max(...dailyTrend.map(x => x.loads), 1);
                        const height = (d.loads / maxLoads) * 100;
                        return (
                          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                            <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', marginBottom: '2px' }}>{d.loads}</div>
                            <div style={{
                              width: '100%', maxWidth: '30px',
                              height: `${Math.max(height, 2)}%`,
                              backgroundColor: d.revenue > 0 ? '#2196f3' : '#4caf50',
                              borderRadius: '2px 2px 0 0',
                              transition: 'height 0.3s',
                            }} title={`${d.date}: ${d.loads} loads, ${fmtMoney(d.revenue)}`} />
                            {i % Math.max(1, Math.floor(dailyTrend.length / 7)) === 0 && (
                              <div style={{ fontSize: '0.55rem', color: 'var(--text-muted)', marginTop: '2px', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                                {d.date.slice(5)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ══════ REVENUE TAB ══════ */}
            {activeTab === 'revenue' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                  {/* By Customer */}
                  <div>
                    <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Revenue by Customer</h3>
                    {revenueByCustomer.length === 0 ? (
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No revenue data</p>
                    ) : (
                      revenueByCustomer.slice(0, 10).map(c => (
                        <Bar key={c.name} label={c.name} value={c.revenue} max={revenueByCustomer[0]?.revenue || 1} color="#2196f3" />
                      ))
                    )}
                  </div>
                  {/* By Driver */}
                  <div>
                    <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Revenue by Driver</h3>
                    {revenueByDriver.length === 0 ? (
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No revenue data</p>
                    ) : (
                      revenueByDriver.slice(0, 10).map(d => (
                        <Bar key={d.name} label={d.name} value={d.revenue} max={revenueByDriver[0]?.revenue || 1} color="#4caf50" />
                      ))
                    )}
                  </div>
                </div>

                {/* Revenue Table */}
                {revenueByCustomer.length > 0 && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Customer Revenue Detail</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>{['Customer', 'Loads', 'Revenue', 'Avg/Load'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {revenueByCustomer.map(c => (
                          <tr key={c.name}>
                            <td style={tdStyle}>{c.name}</td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>{c.loads}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{fmtMoney(c.revenue)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{c.loads > 0 ? fmtMoney(c.revenue / c.loads) : '$0.00'}</td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '2px solid var(--surface-3)' }}>
                          <td style={{ ...tdStyle, fontWeight: 'bold' }}>TOTAL</td>
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>{revenueByCustomer.reduce((s, c) => s + c.loads, 0)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: '#4caf50' }}>{fmtMoney(revenueByCustomer.reduce((s, c) => s + c.revenue, 0))}</td>
                          <td style={tdStyle}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ══════ DRIVERS TAB ══════ */}
            {activeTab === 'drivers' && (
              <div>
                <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Driver Performance Scorecard</h3>
                {scorecard.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)' }}>No driver data for this period</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Driver', 'Loads', 'Done', 'Rate', 'Revenue', '$/Load', 'Distance', 'Fuel', 'Break Min'].map(h => (
                          <th key={h} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {scorecard.map(d => (
                        <tr key={d.name}>
                          <td style={{ ...tdStyle, fontWeight: 'bold' }}>{d.name}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>{d.loads}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>{d.completed}</td>
                          <td style={{ ...tdStyle, textAlign: 'center', color: parseInt(d.completionRate) >= 90 ? '#4caf50' : '#ff9800' }}>{d.completionRate}%</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtMoney(d.revenue)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtMoney(parseFloat(d.avgRevenuePerLoad))}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(d.distanceKm)} km</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{d.fuelL} L</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{d.breakMinutes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* On-time by driver */}
                {onTime && Object.keys(onTime.byDriver).length > 0 && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.75rem', fontSize: '0.95rem' }}>On-Time Delivery by Driver</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>{['Driver', 'On Time', 'Late', 'Total', 'On-Time %'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {Object.entries(onTime.byDriver).map(([name, d]) => (
                          <tr key={name}>
                            <td style={{ ...tdStyle, fontWeight: 'bold' }}>{name}</td>
                            <td style={{ ...tdStyle, textAlign: 'center', color: '#4caf50' }}>{d.onTime}</td>
                            <td style={{ ...tdStyle, textAlign: 'center', color: d.late > 0 ? '#f44336' : 'var(--text-primary)' }}>{d.late}</td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>{d.total}</td>
                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold', color: d.total > 0 && (d.onTime / d.total) >= 0.9 ? '#4caf50' : '#ff9800' }}>
                              {d.total > 0 ? ((d.onTime / d.total) * 100).toFixed(0) : 0}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ══════ BILLING TAB ══════ */}
            {activeTab === 'billing' && billing && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  <KPICard label="Total Distance" value={`${fmt(billing.totalDistanceKm)} km`} color="#2196f3" />
                  <KPICard label="Fuel Used" value={`${fmt(billing.totalFuelLiters)} L`} color="#ff9800" />
                  <KPICard label="Fuel Efficiency" value={`${billing.fuelEfficiency} km/L`} color={parseFloat(billing.fuelEfficiency) > 3 ? '#4caf50' : '#f44336'} />
                  <KPICard label="Distance Charges" value={fmtMoney(billing.totalDistanceCharges)} color="#4caf50" />
                  <KPICard label="Time Charges" value={fmtMoney(billing.totalTimeCharges)} color="#9c27b0" />
                  <KPICard label="Avg Rate/km" value={`$${billing.avgRatePerKm}`} color="#00bcd4" />
                  <KPICard label="Billing Records" value={fmt(billing.records)} color="var(--text-muted)" />
                </div>

                {kpis && (
                  <div style={{ padding: '1rem', backgroundColor: 'var(--surface-2)', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 0.5rem', color: 'var(--accent-primary)' }}>Revenue vs Billing Summary</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.9rem' }}>
                      <div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>LOAD REVENUE</div>
                        <div style={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2rem' }}>{fmtMoney(kpis.totalRevenue)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>BILLING CHARGES</div>
                        <div style={{ color: '#2196f3', fontWeight: 'bold', fontSize: '1.2rem' }}>{fmtMoney(billing.totalDistanceCharges + billing.totalTimeCharges)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

const modalStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const contentStyle = {
  backgroundColor: 'var(--surface-1)', borderRadius: '10px',
  width: '95%', maxWidth: '1100px', maxHeight: '90vh', overflow: 'auto', padding: '2rem',
};
const closeBtn = { background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '1.5rem', cursor: 'pointer' };
const selectStyle = {
  padding: '0.5rem', backgroundColor: 'var(--surface-2)', border: '1px solid var(--border-default)',
  borderRadius: '4px', color: 'var(--text-primary)', fontSize: '0.85rem', cursor: 'pointer',
};
const thStyle = {
  textAlign: 'left', padding: '0.5rem 0.6rem', borderBottom: '2px solid var(--surface-3)',
  fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px',
};
const tdStyle = {
  padding: '0.5rem 0.6rem', borderBottom: '1px solid var(--surface-2)', fontSize: '0.85rem', color: 'var(--text-primary)',
};

export default BIDashboardModal;
